import { meStore } from '@gozoki/api'
import type { StoreCamera, StoreCameraViewSession } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { Camera01Icon } from './icons/dashboard-icons'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { StoreCameraPlayer } from './StoreCameraPlayer'

/** Battement de l'horloge : deux fois par minute, assez pour que l'incrustation ne retarde pas. */
const CLOCK_TICK_MS = 30_000
/** Grille plein écran : 3 colonnes × 2 lignes = exactement 6 caméras par page (Figma), sans scroll. */
const CAMERAS_PER_PAGE = 6

/**
 * Modale « Caméras » (Figma 11393:48967) : un mur de toutes les caméras de la boutique, chacune en
 * direct dès l'ouverture (6 par page), avec son horodatage. Pas de sélection ni d'agrandissement
 * d'une caméra : l'écran de la boutique est déjà grand (demande client du 14/10). Seule la page
 * affichée joue, les caméras des autres pages ne sont pas connectées.
 *
 * Rendue DANS le canvas mis à l'échelle et non via le `Dialog` partagé : celui-ci passe par un
 * portail sur `document.body`, donc hors du `transform: scale()` du dashboard — la modale
 * s'afficherait à l'échelle 1 au milieu d'une interface à ~1,5. Un `position: fixed` sous un
 * ancêtre transformé se cale sur cet ancêtre, pas sur le viewport.
 */
export function CamerasModal({
  cameras,
  session,
  initialCameraName,
  onClose,
}: {
  cameras: StoreCamera[]
  session: StoreCameraViewSession | undefined
  initialCameraName: string | null
  onClose: () => void
}) {
  const { data: me } = useQuery({ queryKey: ['store', 'me'], queryFn: meStore })
  // Pagination : 6 caméras par écran. On ouvre sur la page qui contient la caméra visée (chevron
  // sans caméra → page 0).
  const pageCount = Math.max(1, Math.ceil(cameras.length / CAMERAS_PER_PAGE))
  const [page, setPage] = useState(() => {
    const target = cameras.findIndex((camera) => camera.name === initialCameraName)
    return target !== -1 ? Math.floor(target / CAMERAS_PER_PAGE) : 0
  })
  const [now, setNow] = useState(() => DateTime.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), CLOCK_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const timestamp = now.setZone('Europe/Paris').toFormat('HH:mm - dd.MM.yyyy')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#072C1A]/25 p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label="Caméras de la boutique"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-[1917px] flex-col gap-[16px] overflow-hidden rounded-[24px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête : icône + titre à gauche, bouton fermer à droite. */}
        <div className="flex items-center gap-[24px]">
          <div className="flex min-w-0 flex-1 items-center gap-[12px]">
            <span className="flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#F9F8F1] text-[#4F6A47]">
              <Camera01Icon className="size-[24px]" />
            </span>
            <h2 className="truncate font-brand text-[22px] leading-[26px] font-semibold text-[#4F6A47] uppercase">
              Caméras {me?.store.label ?? ''}
            </h2>
          </div>

          <ModalCloseButton onClose={onClose} ariaLabel="Fermer les caméras" />
        </div>

        <div className="h-px w-full shrink-0 bg-[#0000000F]" />

        {cameras.length > 0 ? (
          // 6 caméras par page (3 colonnes × 2 lignes), au-delà on pagine. Tuiles au format 16:9 des
          // flux : l'image entière, sans bandes noires ni rognage (la modale fait ~2/3 de l'écran,
          // comme le Figma).
          <div className="grid grid-cols-3 gap-[24px]">
            {cameras
              .slice(page * CAMERAS_PER_PAGE, page * CAMERAS_PER_PAGE + CAMERAS_PER_PAGE)
              .map((camera, index) => {
                const globalIndex = page * CAMERAS_PER_PAGE + index
                return (
                  <div key={camera.id} className="flex flex-col items-center gap-[10px]">
                    <p className="w-full shrink-0 truncate text-center font-brand text-[22px] leading-[26px] font-extrabold text-[#6A9560]">
                      {globalIndex + 1} - {camera.label}
                    </p>

                    <div className="relative aspect-video w-full overflow-hidden rounded-[16px] bg-[#2F3D28]">
                      {camera.fullStreamUrl && session ? (
                        <StoreCameraPlayer
                          cameraName={camera.name}
                          session={session}
                          className="absolute inset-0 rounded-[16px]"
                        />
                      ) : (
                        <span className="absolute inset-0 flex items-center justify-center font-sans text-[14px] text-white">
                          Flux indisponible
                        </span>
                      )}

                      {/* Horodatage (Figma 11393:49117) : coin haut-gauche. */}
                      <span className="pointer-events-none absolute top-[16px] left-[16px] rounded-[6px] border border-black/[0.06] bg-[rgba(21,32,19,0.6)] px-[8px] py-[4px] font-sans text-[14px] leading-[16px] font-medium text-[#F9F8F1]">
                        {timestamp}
                      </span>
                    </div>
                  </div>
                )
              })}
          </div>
        ) : (
          <span className="py-[40px] text-center font-sans text-[16px] text-[#3E4F3F]">
            Aucune caméra sur cette boutique.
          </span>
        )}

        {pageCount > 1 ? (
          // Pagination (au-delà de 6 caméras) : une pastille par page, la page courante en vert.
          <div className="flex shrink-0 items-center justify-center gap-[10px]">
            {Array.from({ length: pageCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index)}
                aria-label={`Page ${index + 1} sur ${pageCount}`}
                aria-current={index === page}
                className={cn(
                  'size-[12px] rounded-full transition-colors',
                  index === page ? 'bg-[#4F6A47]' : 'bg-[#C7CFC4] hover:bg-[#9FB199]',
                )}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
